// The return path a sign-in page carries (`?next=`): where a signed-out visit to a deep link goes once the person is in.
// Only a path on this site counts (no scheme, no host, no "//" or "/\"), and never a sign-in page itself. The server checks
// it again before it redirects (client-auth.ts, sso.ts).

const SIGN_IN = /^\/(portal\/)?sign-in(?:[/?#]|$)/;

/** `value` when it is a same-site relative path, else undefined. */
export function safeNext(value: unknown): string | undefined {
  return typeof value === "string" && value.length <= 300 && /^\/(?![/\\])\S*$/.test(value) && !SIGN_IN.test(value) ? value : undefined;
}

/** The return path for the page the person is on now, or undefined when it is the home page (sign-in lands there anyway). */
export function nextFor(path: string, home: string): string | undefined {
  return path === "/" || path === home || path === `${home}/` ? undefined : safeNext(path);
}

/** Where the team sign-in page sends a person who is in (password, passkey, code): /join for an account with no
 *  workspace yet (GET /api/v1/workspace answered 401), else the return path, else /home. */
export function afterSignIn(workspaceStatus: number | undefined, next: string | undefined): string {
  return workspaceStatus === 401 ? "/join" : (safeNext(next) ?? "/home");
}

/** `/sign-in` or `/portal/sign-in` with the return path for `path` (path plus search). */
export function signInHref(signIn: "/sign-in" | "/portal/sign-in", path: string): string {
  const next = nextFor(path, signIn === "/sign-in" ? "/home" : "/portal");
  return next ? `${signIn}?next=${encodeURIComponent(next)}` : signIn;
}
